import { fmtArea, fmtEnergy, fmtPower } from "../../../utils/formatUnits";
import { COORDINATE_DECIMAL_PLACES } from "../../ReportComponent/constants/reportConstants";
import { executiveSummaryText } from "../../ReportComponent/model/summaryText";
import type { EnergyType, ReportSubject } from "../../ReportComponent/model/types";
import { INK, KeyNumber, nf, pct } from "./primitives";

const ENERGY_TITLE: Record<EnergyType, string> = {
  solar: "Solar fotovoltaica",
  eolico: "Eólica",
};

function SubjectHeading({ subject }: { subject: ReportSubject }) {
  const kindLabel =
    subject.kind === "estado"
      ? "Estado"
      : subject.kind === "municipio"
      ? "Município"
      : "Área desenhada";
  return (
    <div className="flex items-baseline justify-between gap-3 mb-1">
      <div>
        <span className="text-[0.7rem] uppercase tracking-wide" style={{ color: INK.secondary }}>
          {kindLabel}
        </span>
        <h3 className="text-[1.05rem] font-bold" style={{ color: INK.brand }}>
          {subject.name}
        </h3>
      </div>
      <div className="text-right text-[0.78rem]" style={{ color: INK.secondary }}>
        {subject.areaKm2 != null && <div>{fmtArea(subject.areaKm2)}</div>}
        {subject.kind === "area" && subject.municipalityName && (
          <div>{subject.municipalityName}</div>
        )}
        {subject.coordinates && (
          <div>
            {subject.coordinates.lat.toFixed(COORDINATE_DECIMAL_PLACES)},{" "}
            {subject.coordinates.lng.toFixed(COORDINATE_DECIMAL_PLACES)}
          </div>
        )}
      </div>
    </div>
  );
}

function EnergyKeyNumbers({
  subject,
  energy,
  levelLabel,
}: {
  subject: ReportSubject;
  energy: EnergyType;
  levelLabel: string;
}) {
  const stats = subject.analyses[energy]?.detailedLevel.stats;
  return (
    <div className="report-section mb-3">
      <p className="text-[0.78rem] font-semibold mb-1.5" style={{ color: INK.brand }}>
        {ENERGY_TITLE[energy]} · nível {levelLabel}
      </p>
      <div className="grid grid-cols-4 gap-2">
        <KeyNumber
          label="Área propícia"
          value={stats ? fmtArea(stats.propitious_area_km2) : "—"}
          detail={stats ? `${pct(stats.percentage)} da área` : undefined}
        />
        <KeyNumber
          label={energy === "solar" ? "Potencial instalável (pico)" : "Potencial instalável"}
          value={stats ? fmtPower(stats.installable_gw) : "—"}
        />
        <KeyNumber
          label="Geração anual estimada"
          value={stats ? fmtEnergy(stats.annual_energy_gwh) : "—"}
        />
        <KeyNumber
          label="Energia específica"
          value={
            stats && stats.propitious_area_km2 > 0
              ? `${nf(stats.annual_energy_gwh / stats.propitious_area_km2, 1)} GWh/km²`
              : "—"
          }
          detail="por km² propício, ao ano"
        />
      </div>
    </div>
  );
}

function GridKeyNumbers({ subject }: { subject: ReportSubject }) {
  const find = (id: string) => subject.infrastructure.find((d) => d.id === id);
  const substation = find("subestacao");
  const line = find("linha_transmissao");
  if (!substation && !line) return null;
  const km = (v: number | null | undefined) => (v == null ? "—" : `${nf(v, 1)} km`);
  return (
    <div className="grid grid-cols-4 gap-2 mb-3 report-section">
      <KeyNumber label={substation?.label ?? "Subestação"} value={km(substation?.distanceKm)} detail="mais próxima" />
      <KeyNumber label={line?.label ?? "Linha de transmissão"} value={km(line?.distanceKm)} detail="mais próxima" />
    </div>
  );
}

export function ExecutiveSummary({
  subject,
  energies,
  levelLabel,
}: {
  subject: ReportSubject;
  energies: EnergyType[];
  levelLabel: string;
}) {
  return (
    <section className="mb-6">
      <SubjectHeading subject={subject} />
      {subject.mapImage && (
        <img
          src={subject.mapImage}
          alt={`Mapa de localização de ${subject.name}`}
          className="w-full h-auto max-h-[340px] object-contain border rounded my-2"
          style={{ borderColor: INK.rule }}
        />
      )}
      <p
        className="report-section text-[0.88rem] leading-relaxed my-3"
        style={{ color: INK.primary }}
      >
        {executiveSummaryText(subject, energies, levelLabel)}
      </p>
      {energies.map((energy) => (
        <EnergyKeyNumbers
          key={energy}
          subject={subject}
          energy={energy}
          levelLabel={levelLabel}
        />
      ))}
      <GridKeyNumbers subject={subject} />
    </section>
  );
}

